import { DashboardProvider, useDashboard } from "@/context/dashboard";
import Dashboard from "@/Layouts/Dashboard";
import { useFormik } from "formik";
import * as Yup from "yup";
import { MdCameraAlt } from "react-icons/md";
import PhoneInput from "react-phone-input-2";
import ReactCrop from "react-image-crop";
import { useEffect, useRef, useState } from "react";
import { decryptInteger, encryptInteger } from "@/Utils/encryptInteger";
import "react-image-crop/dist/ReactCrop.css";
import toast from "react-hot-toast";
import Head from "next/head";
import { subYears } from "date-fns";
import { IoMdClose } from "react-icons/io";
import Cookies from "js-cookie";
import MetaTags from "@/Components/SEO/MetaTags";
import withBaseUrl from "@/Utils/const";
import { loadGoogleMapsScript } from "@/Utils/loadScript";

export default function index({ API_KEY }) {
  return (
    <DashboardProvider>
      <MetaTags
        title="My Profile - Qoncert"
        description="Manage your Qoncert profile, update your information, and view your concert preferences."
        keywords="profile, account settings, user profile, concert preferences, account management"
        canonicalUrl={withBaseUrl("/profile")}
        noindex={true}
      />
      <Head>
        <title>Qoncert App | Shows</title>
      </Head>
      <Profile API_KEY={API_KEY} />
    </DashboardProvider>
  );
}

function Profile({ API_KEY }) {
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);

  const [data, setData] = useState({
    name: "",
    profile: "",
    email: "",
    phone_number: "",
    birthday: "",
    gender: "",
    country_code: "",
    city: "",
    state: "",
    city_data: "",
  });
  const [isColoured, setIsColoured] = useState(false);
  const [makeDisabled, setMakeDisabled] = useState({
    email: false,
    phone_number: false,
    birthday: false,
  });
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const { setIsUpdateProfileInfo } = useDashboard();
  const [crop, setCrop] = useState();
  const autocompleteService = useRef(null);
  const [image, setImage] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [stateData, setSatetData] = useState([]);
  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  const sessionToken = useRef(null);

  useEffect(() => {
    if (API_KEY) {
      loadGoogleMapsScript(API_KEY)
        .then(async () => {
          try {
            const { AutocompleteSuggestion, AutocompleteSessionToken } =
              await google.maps.importLibrary("places");
            autocompleteService.current = AutocompleteSuggestion;
            sessionToken.current = new AutocompleteSessionToken();
          } catch (error) {
            toast.error("Failed to initialize Places service");
          }
        })
        .catch((err) => {
          toast.error("Failed to load Google Maps.");
        });
    } else {
      console.error("No Google Maps API key provided");
    }
    getStatesData();
  }, []);
  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    phone_number: Yup.string().required("Phone number is required"),
    birthday: Yup.date()
      .required("Birthday is required")
      .max(subYears(new Date(), 16), "You must be at least 16 years old"),
    gender: Yup.string().required("Gender is required"),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("State is required"),
    city_data: Yup.string().required("City data is required"),
  });

  const formik = useFormik({
    initialValues: data,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setLoading(true);
      let web_token = "";
      const encryptedData = localStorage.getItem("user_data");
      if (encryptedData) {
        const decryptedString = decryptInteger(encryptedData);
        const userData = JSON.parse(decryptedString);
        web_token = userData.web_token;

        try {
          setData(values);
          const formData = new FormData();
          formData.append("action", "updateMyProfile");
          formData.append("name", values.name);
          formData.append("email", values.email);
          formData.append("phone_number", values.phone_number);
          formData.append("dob", values.birthday);
          formData.append("gender", values.gender);
          formData.append("city", values.city);
          formData.append("state", values.state);
          if (values.profile instanceof File) {
            formData.append("profile", values.profile);
          }

          const response = await fetch(
            `${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageUserData`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${web_token}`,
              },
              body: formData,
            }
          );

          const data = await response.json();

          if (data.status) {
            toast.success(data.message);
            setIsUpdateProfileInfo(true);
            fetchProfileInfo();
          } else {
            toast.error(data.message);
            if (data.code == 201) {
              localStorage.clear();
              Cookies.remove("user_data");
              sessionStorage.clear();
            }
          }
        } catch (error) {
          console.error("Error submitting form", error);
        } finally {
          setLoading(false);
        }
      }
    },
  });

  const handleInputChange = async (value) => {
    formik.setFieldValue("city_data", value);

    if (!value || value.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (!autocompleteService.current) {
      console.log("Google Maps not loaded yet, waiting...");
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const request = {
        input: value,
        includedRegionCodes: ["us"],
        includedPrimaryTypes: ["locality"],
        sessionToken: sessionToken.current,
        language: "en",
      };

      const response =
        await autocompleteService.current.fetchAutocompleteSuggestions(request);

      if (response && response.suggestions) {
        setSuggestions(response.suggestions);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = async (index) => {
    try {
      const suggestion = suggestions[index];

      if (suggestion && suggestion.placePrediction) {
        const mainText =
          suggestion.placePrediction.text?.text ||
          suggestion.placePrediction.mainText?.text ||
          "";

        const parts = mainText.split(", ");
        if (parts.length >= 2) {
          const city = parts[0].trim();
          const statePart = parts[1].trim();

          const state = statePart.split(" ")[0];

          const formattedAddress = `${city}, ${state}`;
          formik.setFieldValue("city_data", formattedAddress);
          formik.setFieldValue("state", state);
          formik.setFieldValue("city", city);

          const { AutocompleteSessionToken } = await google.maps.importLibrary(
            "places"
          );
          sessionToken.current = new AutocompleteSessionToken();
        } else {
          formik.setFieldValue("city_data", mainText);
          formik.setFieldValue("city", mainText);
        }
      } else {
        console.error("Invalid suggestion structure:", suggestion);
        toast.error("Invalid location data received");
      }
    } catch (error) {
      console.error("Error getting place details:", error);
      toast.error("Error getting location details");
    }

    setShowSuggestions(false);
  };

  const getStatesData = async () => {
    try {
      const formData = new FormData();
      formData.append("action", "getAllState");

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageState`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();
      setSatetData(data.data);
    } catch (error) {
      console.error("API Error:", error.message);
    }
  };
  useEffect(() => {
    setIsColoured(formik.isValid && formik.dirty);
  }, [formik.isValid, formik.dirty]);

  const handleDivClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target.result;
        img.onload = () => {
          setImage(img.src);
          const cropWidth = 200;
          const cropHeight = 200;
          const x = (img.width - cropWidth) / 2;
          const y = (img.height - cropHeight) / 2;

          setCrop({
            unit: "px",
            width: cropWidth,
            height: cropHeight,
            x: 0,
            y: 0,
          });
        };
      };
      reader.readAsDataURL(file);
    }
  };

  const getCroppedImage = () => {
    if (!imgRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const imageElement = imgRef.current;
    const ctx = canvas.getContext("2d");

    let crop = completedCrop;

    const scaleX = imageElement.naturalWidth / imageElement.width;
    const scaleY = imageElement.naturalHeight / imageElement.height;

    canvas.width = crop.width;
    canvas.height = crop.height;

    ctx.drawImage(
      imageElement,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height
    );

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], "cropped-image.jpg", {
          type: "image/jpeg",
        });
        formik.setFieldValue("profile", file);
      }
    }, "image/jpeg");
  };

  useEffect(() => {
    setLoading(true);
    fetchProfileInfo();
  }, []);

  const fetchProfileInfo = async () => {
    try {
      let web_token = "";
      const encryptedData = localStorage.getItem("user_data");
      if (encryptedData) {
        const decryptedString = decryptInteger(encryptedData);
        const userData = JSON.parse(decryptedString);

        if (!userData) {
          toast.error("Seestion expired, Please Login Again..");
        } else {
          web_token = userData.web_token;
          const formdata = new FormData();
          formdata.append("action", "fetchProfileInfo");

          const response = await fetch(
            `${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageUserData`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${web_token}`,
              },
              body: formdata,
            }
          );

          const data = await response.json();
          if (data.status) {
            const values = data.data;

            const storingData = {
              profile: values.profile,
              web_token_expires_at: values.web_token_expires_at,
              web_token: values.web_token,
              name: values?.name,
              state: values?.state,
              user_type: values?.user_type,
            };

            const encryptedData = encryptInteger(JSON.stringify(storingData));

            localStorage.setItem("user_data", encryptedData);

            setData({
              name: values.name ?? "",
              email: values.email ?? "",
              phone_number: values.phone_number ?? "",
              birthday: values.dob ?? "",
              gender: values.gender ?? "",
              profile: values.profile ?? "",
              country_code: values?.country_code ?? "",
              city: values?.city ?? "",
              state: values?.state ?? "",
              city_data: `${values?.city}, ${values?.state}` ?? "",
            });

            setMakeDisabled({
              email: !!values.email,
              phone_number: !!values.phone_number,
              birthday: !!values.dob,
            });
          } else {
            console.log("Error fetching profile info");
          }
        }
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dashboard>
      <div
        id="profile-container"
        className={`custom_scrll_bar lg:max-h-[calc(100vh-90px)] pt-5 lg:pt-5 px-10 pb-4 overflow-auto`}
      >
        <h2 className="dashboard-heading pl-3 lg:pl-0 mt-0 ">Profile</h2>
        <div className="mt-7 rounded-[16px] w-full max-w-[993px] mx-auto bg-[#0D0C0F] md:bg-[#1F222A]">
          <form onSubmit={formik.handleSubmit} className="h-full relative">
            {loading && (
              <div className="h-[565px] lg:h-[672px] bg-[#0d0d10] lg:bg-[#1f222ad6] z-[50] rounded-2xl w-full absolute top-0 flex justify-center items-center ">
                <div className="h-fit">
                  <span className="loader"></span>
                </div>
              </div>
            )}
            <div className="w-full md:py-[31px] flex justify-center">
              <div className="max-w-[670px] w-full">
                <div className="flex flex-col cursor-pointer md:px-4 lg:justify-center items-center">
                  <div className="flex flex-col justify-center items-center">
                    <div
                      className="w-[80px] h-[80px] bg-white flex justify-center items-center rounded-full overflow-hidden"
                      onClick={handleDivClick}
                    >
                      {formik.values.profile ? (
                        <img
                          src={
                            typeof formik.values.profile === "string"
                              ? formik.values.profile
                              : URL.createObjectURL(formik.values.profile)
                          }
                          alt="Uploaded"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <MdCameraAlt className="text-[#414141] text-[28px]" />
                      )}
                    </div>
                    <div
                      onClick={handleDivClick}
                      className="mt-4 hidden md:block"
                    >
                      <p className="text-[#FA2CB4] upload-photo">
                        Upload Photo
                      </p>
                    </div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      accept="image/*"
                      onChange={handleFileChange}
                    />

                    {image && (
                      <div className="fixed inset-0 flex justify-center z-[999] items-center bg-black/50">
                        <div className="bg-white flex flex-col p-4 rounded-lg">
                          <div className="flex justify-end  py-3">
                            <IoMdClose
                              onClick={() => {
                                setImage(null);
                                setCrop(null);
                                if (fileInputRef.current) {
                                  fileInputRef.current.value = "";
                                }
                              }}
                              className="text-black text-2xl cursor-pointer"
                            />
                          </div>
                          <ReactCrop
                            crop={crop}
                            onChange={(newCrop) => setCrop(newCrop)}
                            onComplete={(c) => setCompletedCrop(c)}
                            aspect={1}
                          >
                            <img
                              ref={imgRef}
                              src={image}
                              alt="Crop"
                              style={{ maxWidth: "400px", minHeight: "300px" }}
                            />
                          </ReactCrop>
                          <button
                            onClick={() => {
                              getCroppedImage();
                              setImage(null);
                              setCrop(null);
                              if (fileInputRef.current) {
                                fileInputRef.current.value = "";
                              }
                            }}
                            className="mt-2 p-2 main_btn_bg bg-blue-500 text-white rounded"
                          >
                            Crop & Save
                          </button>
                        </div>
                      </div>
                    )}

                    <canvas ref={canvasRef} style={{ display: "none" }} />
                  </div>

                  <div className="grid w-full mt-6 md:grid-cols-2 gap-x-12 gap-y-6 lg:gap-y-11">
                    <div className="">
                      <label className="hidden md:block" htmlFor="name">
                        Name
                      </label>
                      <div className="md:mt-4">
                        <input
                          type="text"
                          name="name"
                          id="name"
                          className="bg-[#1F222A] md:bg-[#35383F]  w-full px-4 py-3 rounded-[16px] border border-[#CFCFCF1D] outline-none text-[#B6B6B6]"
                          placeholder="Enter your first name"
                          value={formik.values.name}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.name && formik.errors.name && (
                          <p className="text-red-500 text-sm">
                            {formik.errors.name}
                          </p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="hidden md:block" htmlFor="email">
                        Your Email
                      </label>
                      <div className="md:mt-4">
                        <input
                          type="email"
                          name="email"
                          id="email"
                          className={`bg-[#2121219b] md:bg-[#2121219b]  w-full px-4 py-3 rounded-[16px] border border-[#CFCFCF1D] outline-none text-[#B6B6B6]`}
                          placeholder="Enter your email"
                          value={formik.values.email}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          disabled={makeDisabled.email}
                        />
                        {formik.touched.email && formik.errors.email && (
                          <p className="text-red-500 text-sm">
                            {formik.errors.email}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="">
                      <label className="hidden md:block" htmlFor="name">
                        City
                      </label>
                      <div className="md:mt-4">
                        <div className="relative">
                          <input
                            ref={inputRef}
                            className="bg-[#1F222A] md:bg-[#35383F] w-full px-4 py-3 rounded-[16px] border border-[#CFCFCF1D] outline-none text-[#B6B6B6]"
                            placeholder="Type address"
                            value={formik.values.city_data || ""}
                            onChange={(e) => handleInputChange(e.target.value)}
                            onFocus={() =>
                              formik.values.city_data &&
                              setShowSuggestions(true)
                            }
                            onBlur={() =>
                              setTimeout(() => setShowSuggestions(false), 200)
                            }
                          />

                          {showSuggestions && suggestions.length > 0 && (
                            <div className="absolute top-full z-[9999] w-full max-h-[250px] overflow-auto rounded-[5px] bg-white">
                              {suggestions.map((suggestion, i) => (
                                <div
                                  key={suggestion.placePrediction?.placeId || i}
                                  className={`text-black hover:bg-blue-400 hover:text-white cursor-pointer p-2 px-4 
                                  ${i === 0 ? "rounded-t-[5px]" : ""} 
                                  ${i === suggestions.length - 1
                                      ? "rounded-b-[5px]"
                                      : "border-b"
                                    }`}
                                  onClick={() => handleSuggestionClick(i)}
                                >
                                  {suggestion.placePrediction?.text?.text ||
                                    suggestion.placePrediction?.mainText?.text}
                                </div>
                              ))}
                            </div>
                          )}

                          {formik.touched.city_data &&
                            formik.errors.city_data ? (
                            <div className="text-red-500 text-sm">
                              {formik.errors.city_data}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 ">
                      <label
                        className="text-[10px] lg:text-base text-[#718096]"
                        htmlFor="phone_number"
                      >
                        Phone number
                      </label>
                      <input
                        type="hidden"
                        name="country_code"
                        value={data.country_code}
                      />
                      <div className="flex w-full gap-4 items-start ">
                        <div className="w-[48px] h-[48px] ">
                          <PhoneInput
                            country={"us"}
                            value={data.country_code}
                            onChange={(value, country) => {
                              setData({
                                ...data,
                                country_code: `+ ${country.dialCode} `,
                              });
                            }}
                            inputStyle={{
                              display: "none",
                            }}
                            buttonStyle={{
                              borderRadius: "6px",
                              height: "40px",
                              border: "1px solid #ccc",
                            }}
                            dropdownStyle={{
                              zIndex: 9999,
                            }}
                            disableCountryCode={true}
                            disableDropdown={false}
                            enableAreaCodes={false}
                            className="min-w-[38px] detailsCustomInput "
                          />
                        </div>
                        <input
                          type="text"
                          name="phone_number"
                          placeholder="123-456-7890"
                          className="bg-[#36383F] w-full text-white focus:outline-none text-sm lg:text-base rounded-[5px] p-3"
                          value={data.phone_number}
                          pattern="^[0-9\-]*$"
                          title="Only numbers and dashes are allowed"
                          disabled={makeDisabled.phone_number}
                          onChange={(e) => {
                            const cleaned = e.target.value.replace(/[^0-9\-]/g, "");
                            formik.setFieldValue("phone_number", cleaned);
                          }}
                        />

                      </div>

                      {formik.touched.phone_number && formik.errors.phone_number && (
                        <p className="text-red-500 text-sm">
                          {formik.errors.phone_number}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="hidden md:block" htmlFor="birthday">
                        Birth Date
                      </label>
                      <div className="md:mt-4">
                        <input
                          type="date"
                          name="birthday"
                          id="birthday"
                          className={`${makeDisabled.birthday
                              ? "bg-[#2121219b] md:bg-[#2121219b] "
                              : "bg-[#1F222A] md:bg-[#35383F] "
                            } birthdayPicker   rounded-[16px] border border-[#CFCFCF1D] outline-none text-[#B6B6B6]`}
                          value={
                            formik.values.birthday ||
                            new Date().toISOString().slice(0, 10)
                          }
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          disabled={makeDisabled.birthday}
                          placeholder="Enter Birthday"
                        />
                        {formik.touched.birthday && formik.errors.birthday && (
                          <p className="text-red-500 text-sm">
                            {formik.errors.birthday}
                          </p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="hidden md:block" htmlFor="gender">
                        Gender
                      </label>
                      <div className="md:mt-4">
                        <select
                          name="gender"
                          id="gender"
                          className="bg-[#1F222A] md:bg-[#35383F] birthdayPicker rounded-[16px] border border-[#CFCFCF1D] outline-none text-[#B6B6B6]"
                          value={formik.values.gender}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        >
                          <option value="" disabled>
                            Select Gender
                          </option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                        {formik.touched.gender && formik.errors.gender && (
                          <p className="text-red-500 text-sm">
                            {formik.errors.gender}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-12 mb-7 ">
                    <button
                      type="submit"
                      className={`w-[238px] rounded-[16px] ${isColoured ? "colored-button" : ""
                        } text-white`}
                    >
                      <p className="py-3">Save</p>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Dashboard>
  );
}

export async function getStaticProps() {
  try {
    const KEY = process.env.NEXT_PUBLIC_GOOGLE_MAP_KEY;

    return {
      props: {
        API_KEY: KEY,
      },
      revalidate: 43200,
    };
  } catch (error) {
    console.error("Error fetching API key:", error);
    return {
      props: {
        API_KEY: null,
        error: error.message,
      },
    };
  }
}
